import { debounce } from '../../core/utils/debounce.js';
import { createAshpApi } from '../../core/api/ashpApi.js';
import { applyPartnerBranding, hydratePartnerBranding, resolvePartnerContext } from '../../core/utils/partnerBranding.js';
import { preserveGrowthCorrelation } from '../../core/utils/params.js';
import { preserveConsentedGoogleAdsAttribution } from '../../core/growth/googleAdsAttribution.js';

const DEFAULT_QUESTIONS = [
    {
        id: 'switchReason',
        key: 'switchReason',
        title: 'Why are you looking to upgrade your heating?',
        type: 'single',
        helperBox: {
            title: 'Why we ask',
            bullets: [
                'This helps us frame the recommendation and follow-up.',
                'It does not directly change your estimate.'
            ]
        },
        options: [
            { value: 'replace_boiler', label: 'Replacing an old boiler' },
            { value: 'renovation', label: 'Renovation or extension' },
            { value: 'lower_bills', label: 'Lower running costs' },
            { value: 'reduce_carbon', label: 'Reduce carbon emissions' }
        ]
    },
    {
        id: 'fuelToday',
        key: 'fuelToday',
        title: 'What type of central heating do you have today?',
        type: 'single',
        helperBox: {
            title: 'Why this matters',
            bullets: [
                'Fuel type affects running-cost comparisons.',
                'Some systems need extra installation work, especially direct electric.'
            ]
        },
        options: [
            { value: 'Mains gas', label: 'Mains gas', icon: 'flame', iconAlt: 'Gas flame icon' },
            { value: 'Oil', label: 'Oil', icon: 'oil-drop', iconAlt: 'Oil drop icon' },
            { value: 'LPG', label: 'LPG', icon: 'gas-bottle', iconAlt: 'LPG bottle icon' },
            { value: 'Direct electric', label: 'Direct electric', icon: 'bolt', iconAlt: 'Electric bolt icon' },
            { value: 'Other', label: 'Other' }
        ]
    },
    {
        id: 'heatingSystem',
        key: 'heatingSystem',
        title: 'What kind of heat emitters do you have?',
        type: 'single',
        helperBox: {
            title: 'What we are checking',
            bullets: [
                'Emitter type affects flow temperature and radiator upgrades.',
                'If you have a mix of radiators and underfloor, choose the mixed option.'
            ]
        },
        options: [
            { value: 'radiators', label: 'Radiators', icon: 'radiator', iconAlt: 'Radiator icon' },
            { value: 'underfloor', label: 'Underfloor heating', icon: 'underfloor', iconAlt: 'Underfloor heating icon' },
            { value: 'mixed', label: 'A mix of both', icon: 'mixed-emitters', iconAlt: 'Mixed emitters icon' }
        ]
    },
    {
        id: 'existingHotWaterCylinder',
        key: 'existingHotWaterCylinder',
        title: 'Do you already have a hot water cylinder?',
        type: 'single',
        helperBox: {
            title: 'Why this matters',
            bullets: [
                'Reusing a compatible cylinder can reduce upfront cost.',
                'Some existing cylinders are not suitable for a heat pump.'
            ]
        },
        options: [
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No' },
            { value: 'unknown', label: 'Not sure' }
        ]
    },
    {
        id: 'cylinderSpace',
        key: 'cylinderSpace',
        title: 'Do you have space for a hot water cylinder?',
        type: 'single',
        when: {
            key: 'existingHotWaterCylinder',
            equals: 'no'
        },
        options: [
            { value: 'true', label: 'Yes' },
            { value: 'false', label: 'No' }
        ]
    },
    {
        id: 'thermostat',
        key: 'thermostat',
        title: 'What temperature do you usually heat your home to?',
        type: 'single',
        options: [18, 19, 20, 21, 22, 23].map(value => ({ value, label: `${value}°C` }))
    },
    {
        id: 'propertyForm',
        key: 'propertyForm',
        title: 'Which best describes your home?',
        type: 'single',
        options: [
            { value: 'Detached', label: 'Detached' },
            { value: 'Semi-Detached', label: 'Semi-detached' },
            { value: 'Mid-Terrace', label: 'Mid-terrace' },
            { value: 'End-Terrace', label: 'End-terrace' },
            { value: 'Bungalow', label: 'Bungalow' },
            { value: 'Flat', label: 'Flat' }
        ]
    },
    {
        id: 'propertyAge',
        key: 'propertyAge',
        title: 'When was your home built?',
        type: 'single',
        options: [
            { value: 'pre_1930', label: 'Pre 1930' },
            { value: '1930_1989', label: '1930-1989' },
            { value: '1990_2006', label: '1990-2006' },
            { value: 'after_2006', label: 'After 2006' },
            { value: 'unknown', label: 'Not sure' }
        ]
    },
    {
        id: 'beds',
        key: 'beds',
        title: 'How many bedrooms does your home have?',
        type: 'single',
        options: [
            { value: 1, label: '1 bedroom' },
            { value: 2, label: '2 bedrooms' },
            { value: 3, label: '3 bedrooms' },
            { value: 4, label: '4 bedrooms' },
            { value: 5, label: '5 bedrooms' },
            { value: 6, label: '6+ bedrooms' }
        ]
    },
    {
        id: 'gardenSpace',
        key: 'gardenSpace',
        title: 'Do you have outside space for a heat pump unit?',
        type: 'single',
        options: [
            { value: 'true', label: 'Yes' },
            { value: 'false', label: 'No' }
        ]
    }
];

function resolveConfig(root) {
    const baseUrl = root?.dataset?.apiBase || root?.dataset?.baseUrl || 'https://api.dwellow.co.uk';
    const { partnerId, source: partnerSource } = resolvePartnerContext(root);
    const siteKey = root?.dataset?.siteKey || '';
    const resultsPath = root?.dataset?.resultsPath || '';
    const resultsUrl = root?.dataset?.resultsUrl || '';
    return { baseUrl, partnerId, partnerSource, siteKey, resultsPath, resultsUrl };
}

function normalizeQuestion(raw, index) {
    if (!raw || typeof raw !== 'object') return null;
    const id = String(raw.id ?? raw.key ?? `q${index + 1}`);
    const key = String(raw.key ?? id);
    const title = raw.title ?? raw.label ?? raw.question ?? 'Question';
    const subtitle = raw.subtitle ?? raw.help ?? '';
    const helperRaw = raw.helperBox ?? raw.helper ?? raw.infoBox ?? null;
    const options = Array.isArray(raw.options)
        ? raw.options.map((opt, idx2) => ({
            value: opt?.value ?? opt?.id ?? String(idx2),
            label: opt?.label ?? opt?.title ?? String(opt?.value ?? idx2),
            sub: opt?.sub ?? opt?.description ?? '',
            next: opt?.next != null ? String(opt.next) : null,
            icon: opt?.icon ?? opt?.iconUrl ?? opt?.image ?? '',
            iconAlt: opt?.iconAlt ?? opt?.label ?? opt?.title ?? ''
        }))
        : [];
    return {
        id,
        key,
        title,
        subtitle,
        type: raw.type ?? 'single',
        options,
        next: raw.next != null ? String(raw.next) : null,
        when: raw.when ?? raw.visibleIf ?? null,
        helperBox: normalizeHelperBox(helperRaw)
    };
}

function normalizeHelperBox(raw) {
    if (!raw || typeof raw !== 'object') return null;
    const title = raw.title ?? raw.heading ?? '';
    const bulletsSource = Array.isArray(raw.bullets)
        ? raw.bullets
        : Array.isArray(raw.points)
            ? raw.points
            : [];
    const bullets = bulletsSource
        .map(item => String(item ?? '').trim())
        .filter(Boolean);

    if (!title && !bullets.length) return null;

    return {
        title,
        bullets
    };
}

function matchesCondition(condition, answers) {
    if (!condition || typeof condition !== 'object') return true;
    const key = condition.key ?? condition.questionKey ?? condition.id;
    if (!key) return true;
    const actual = answers?.[key];
    if (Array.isArray(condition.equalsAny)) {
        return condition.equalsAny.map(String).includes(String(actual));
    }
    if (condition.equals != null) {
        return String(actual) === String(condition.equals);
    }
    if (condition.notEquals != null) {
        return String(actual) !== String(condition.notEquals);
    }
    return true;
}

function buildQuestionFlow(questionSet) {
    const source = Array.isArray(questionSet?.questions) ? questionSet.questions : DEFAULT_QUESTIONS;
    const list = source.map(normalizeQuestion).filter(Boolean);
    const map = new Map();
    const order = [];
    for (const question of list) {
        map.set(question.id, question);
        order.push(question.id);
    }
    return {
        list,
        map,
        order,
        firstId: order[0] ?? null
    };
}

function getVisibleQuestionIds(flow, answers = {}) {
    const order = flow?.order ?? [];
    const map = flow?.map;
    return order.filter(questionId => {
        const question = map?.get(questionId);
        return question ? matchesCondition(question.when, answers) : false;
    });
}

function getNextQuestionId(question, value, flow, answers) {
    if (question?.options) {
        const option = question.options.find(opt => String(opt.value) === String(value));
        if (option?.next) return option.next;
    }
    if (typeof question?.next === 'string') return question.next;
    const visibleIds = getVisibleQuestionIds(flow, answers);
    const idx = visibleIds.indexOf(question.id);
    if (idx >= 0 && idx < visibleIds.length - 1) {
        return visibleIds[idx + 1];
    }
    return 'address';
}

function buildHelperContent(question, value) {
    const key = question?.key;
    if (!key) return null;
    if (key === 'propertyForm' && value === 'Flat') {
        return {
            title: 'Flat or apartment',
            body: 'We can still give an estimate, but flats often need a quick check on permissions, access, and outdoor unit position.'
        };
    }
    if (value === 'unknown' || value === 'Not sure') {
        return {
            title: 'Not sure is fine',
            body: 'We will use a cautious assumption for now and flag anything that needs review later in the journey.'
        };
    }
    if (key === 'propertyAge' && value === 'unknown') {
        return {
            title: 'Age unknown',
            body: 'We will use a reasonable mid-range assumption until we can anchor the estimate to property data.'
        };
    }
    return null;
}

function shouldRouteToContact(question, value) {
    const key = String(question?.key ?? '');
    const normalizedValue = String(value ?? '').trim().toLowerCase();
    const normalizedLabel = String(
        question?.options?.find(opt => String(opt.value) === String(value))?.label ?? ''
    ).trim().toLowerCase();

    if (key === 'propertyForm' && normalizedValue === 'flat') return true;
    return ['unknown', 'not_sure'].includes(normalizedValue) || normalizedLabel === 'not sure';
}

function buildContactContent(question, value) {
    if (!question) return null;
    const key = String(question.key ?? '');

    if (key === 'propertyForm' && String(value) === 'Flat') {
        return {
            title: 'Your home may need a quick review',
            bullets: [
                'Flats often need a quick check on permissions, access, and outdoor unit position.',
                'We can still help, but this type of property usually needs a short follow-up before we show an estimate.'
            ]
        };
    }

    return {
        title: 'A quick review will help us keep this accurate',
        bullets: [
            'You selected an answer that needs a more cautious assumption.',
            'A short follow-up helps us avoid showing a misleading estimate.'
        ]
    };
}

export function registerAshpJourney() {
    if (typeof document === 'undefined') return;

    const createStore = () => {
        if (typeof window !== 'undefined' && window.__ashpJourneyState) {
            return window.__ashpJourneyState;
        }

        const state = ({
        step: 'intro',
        loading: false,
        initialized: false,
        error: null,
        started: false,
        jobId: null,
        api: null,
        partnerId: null,
        partnerSource: 'default',
        baseUrl: null,
        siteKey: '',
        resultsPath: '',
        resultsUrl: '',
        questionSet: null,
        questionFlow: null,
        currentQuestionId: null,
        answers: {},
        selectedOptionValue: null,
        helperContent: null,
        contactContent: null,
        transitionDelayMs: 220,
        history: [],
        isTransitioning: false,
        transitionTimer: null,
        transitionToken: 0,
        navToken: 0,
        addressQuery: '',
        addressResults: [],
        addressSearching: false,
        address: null,
        selectedAddressUdprn: null,
        weather: null,
        branding: null,
        brandingReady: false,
        loaderState: null,
        loaderTimers: [],
        debouncedAddressSearch: null,

        init() {
            if (this.initialized) return;
            this.initialized = true;
            const root = this.$root;
            if (root?.dataset?.ashpJourneyInitialized === 'true') return;
            if (root?.dataset) {
                root.dataset.ashpJourneyInitialized = 'true';
            }
            const { baseUrl, partnerId, partnerSource, siteKey, resultsPath, resultsUrl } = resolveConfig(root);
            this.baseUrl = baseUrl;
            this.partnerId = partnerId;
            this.partnerSource = partnerSource;
            this.siteKey = siteKey;
            this.resultsPath = resultsPath;
            this.resultsUrl = resultsUrl;
            this.api = createAshpApi({ baseUrl, siteKey });
            this.branding = applyPartnerBranding({}, root);
            this.brandingReady = !this.partnerId;
            hydratePartnerBranding({
                api: this.api,
                partnerId: this.partnerId,
                root
            }).then(result => {
                this.branding = result?.branding ?? this.branding;
                if (result?.partnerId) {
                    this.partnerId = result.partnerId;
                }
                this.brandingReady = true;
            }).catch(() => {
                this.brandingReady = true;
            });
            this.debouncedAddressSearch = debounce(() => this.searchAddress(), 200);
        },

        get currentQuestion() {
            return this.questionFlow?.map?.get(this.currentQuestionId) ?? null;
        },

        get visibleQuestionIds() {
            return getVisibleQuestionIds(this.questionFlow, this.answers);
        },

        get totalSteps() {
            return (this.visibleQuestionIds.length || DEFAULT_QUESTIONS.length) + 3;
        },

        get currentStepIndex() {
            if (this.step === 'intro') return 1;
            if (this.step === 'question') {
                const idx = Math.max(0, this.visibleQuestionIds.indexOf(this.currentQuestionId));
                return 2 + idx;
            }
            if (this.step === 'address') return this.visibleQuestionIds.length + 2;
            if (this.step === 'contact') return this.visibleQuestionIds.length + 2;
            if (this.step === 'loadingQuote') return this.visibleQuestionIds.length + 3;
            if (this.step === 'done') return this.totalSteps;
            return 1;
        },

        get progressPct() {
            return Math.min(100, Math.max(0, Math.round((this.currentStepIndex / this.totalSteps) * 100)));
        },

        get brandName() {
            return this.branding?.name || 'dwellow';
        },

        get brandLogoUrl() {
            return this.branding?.logoUrl || '';
        },

        get brandLogoLinkUrl() {
            return this.branding?.logoLinkUrl || '';
        },

        get showWhichLogo() {
            return this.branding?.isWhich === true;
        },

        get showGenericLoader() {
            return this.branding?.isGenericLoader === true;
        },

        get landingBullet1() {
            return this.branding?.landingBullet1 || '';
        },

        get landingBullet2() {
            return this.branding?.landingBullet2 || '';
        },

        get landingBullet3() {
            return this.branding?.landingBullet3 || '';
        },

        get partnershipSubTitle() {
            return this.branding?.partnershipSubTitle || '';
        },

        get showDwellowTrustpilot() {
            return this.branding?.isDwellowTrustpilot === true;
        },

        get showGenericNav() {
            return this.branding?.isGenericNav === true;
        },

        get showMadeInBritain() {
            return this.branding?.isMadeInBritain === true;
        },

        get showContactExplain() {
            return this.branding?.isContactExplain === true;
        },

        get canGoBack() {
            if (this.step === 'question') return (this.visibleQuestionIds.indexOf(this.currentQuestionId) ?? 0) > 0;
            return this.step === 'address' || this.step === 'loadingQuote' || this.step === 'contact';
        },

        get showHelperSection() {
            return Boolean(this.activeHelperContent?.title || this.activeHelperContent?.body || this.activeHelperContent?.bullets?.length);
        },

        get activeHelperContent() {
            return this.helperContent ?? this.currentQuestion?.helperBox ?? null;
        },

        optionIsSelected(value) {
            return String(this.selectedOptionValue) === String(value);
        },

        go(step, meta = {}, { delayMs = 0 } = {}) {
            if (this.isTransitioning) return;
            if (this.transitionTimer) {
                clearTimeout(this.transitionTimer);
                this.transitionTimer = null;
            }
            const token = ++this.transitionToken;
            const apply = () => {
                if (token !== this.transitionToken) return;
                this.history.push({ step: this.step, questionId: this.currentQuestionId });
                this.step = step;
                if (Object.prototype.hasOwnProperty.call(meta, 'questionId')) {
                    this.currentQuestionId = meta.questionId;
                }
                this.isTransitioning = false;
            };
            if (delayMs > 0) {
                this.isTransitioning = true;
                this.transitionTimer = setTimeout(() => {
                    this.transitionTimer = null;
                    apply();
                }, delayMs);
                return;
            }
            apply();
        },

        back() {
            this.clearLoaderTimers();
            if (this.transitionTimer) {
                clearTimeout(this.transitionTimer);
                this.transitionTimer = null;
                this.isTransitioning = false;
            }
            this.transitionToken += 1;
            this.navToken += 1;
            this.error = null;
            this.selectedOptionValue = null;
            this.helperContent = null;
            const prev = this.history.pop();
            if (!prev) return;
            if (this.step === 'question') {
                const currentIndex = this.visibleQuestionIds.indexOf(this.currentQuestionId);
                if (currentIndex > 0) {
                    this.step = 'question';
                    this.currentQuestionId = this.visibleQuestionIds[currentIndex - 1];
                    return;
                }
            }
            this.step = prev.step;
            this.currentQuestionId = prev.questionId;
            if (this.step !== 'contact') {
                this.contactContent = null;
            }
            if (this.step === 'loadingQuote') {
                this.step = 'address';
                return;
            }
        },

        async startJourney(lead = {}) {
            if (this.loading || this.jobId) return;
            this.loading = true;
            this.error = null;
            try {
                const res = await this.api.init({
                    partnerId: this.partnerId,
                    lead
                });
                this.jobId = res.jobId;
                this.questionSet = res.questionSet;
                this.questionFlow = buildQuestionFlow(res.questionSet);
                this.currentQuestionId = this.questionFlow.firstId;
                this.started = true;
                this.go('question', { questionId: this.currentQuestionId });
            } catch (err) {
                this.error = err.message || 'Unable to start journey.';
            } finally {
                this.loading = false;
            }
        },

        async handleAnswer(value) {
            const question = this.currentQuestion;
            if (!question) return;
            const token = this.navToken;
            this.answers[question.key] = value;
            this.selectedOptionValue = value;
            this.helperContent = buildHelperContent(question, value);

            if (shouldRouteToContact(question, value)) {
                this.contactContent = buildContactContent(question, value);
                await this.saveAnswers();
                if (token !== this.navToken) return;
                this.selectedOptionValue = null;
                this.helperContent = null;
                this.go('contact', {}, { delayMs: this.transitionDelayMs });
                return;
            }

            const nextQuestionId = getNextQuestionId(question, value, this.questionFlow, this.answers);

            if (nextQuestionId && nextQuestionId !== 'address') {
                if (token !== this.navToken) return;
                this.selectedOptionValue = null;
                this.helperContent = null;
                this.go('question', { questionId: nextQuestionId }, { delayMs: this.transitionDelayMs });
                return;
            }

            this.selectedOptionValue = null;
            this.helperContent = null;
            await this.saveAnswers();
            if (token !== this.navToken) return;
            this.go('address', {}, { delayMs: this.transitionDelayMs });
        },

        async saveAnswers() {
            if (!this.jobId) return null;
            return this.api.saveAnswers({
                jobId: this.jobId,
                answers: this.answers
            });
        },

        onAddressInput() {
            if (typeof this.debouncedAddressSearch === 'function') {
                this.debouncedAddressSearch();
            }
        },

        async searchAddress() {
            const query = this.addressQuery.trim();
            if (query.length < 3) {
                this.addressResults = [];
                return;
            }
            this.addressSearching = true;
            this.error = null;
            try {
                const res = await this.api.searchAddress({ query });
                this.addressResults = Array.isArray(res?.results) ? res.results : [];
            } catch (err) {
                this.error = err.message || 'Address search failed.';
                this.addressResults = [];
            } finally {
                this.addressSearching = false;
            }
        },

        async selectAddress(udprn) {
            if (!udprn || !this.jobId) return;
            this.loading = true;
            this.error = null;
            this.selectedAddressUdprn = udprn;
            try {
                const res = await this.api.saveAddress({
                    jobId: this.jobId,
                    udprn,
                    ...this.answers
                });
                this.address = res?.jobProperty ?? null;
                this.weather = res?.weather ?? null;
                this.startLoaderSequence();
                this.go('loadingQuote');
                const target = this.resultsPath || this.resultsUrl;
                if (target) {
                    const url = preserveConsentedGoogleAdsAttribution(preserveGrowthCorrelation(target));
                    url.searchParams.set('jobId', this.jobId);
                    if (this.partnerId && this.partnerSource === 'query') {
                        url.searchParams.set('partner', this.partnerId);
                    }
                    this.loaderTimers.push(setTimeout(() => {
                        window.location.href = url.toString();
                    }, 2200));
                    return;
                }
                this.loaderTimers.push(setTimeout(() => {
                    this.step = 'done';
                }, 2200));
            } catch (err) {
                this.error = err.message || 'Unable to save address.';
                this.step = 'address';
            } finally {
                this.loading = false;
            }
        },

        clearLoaderTimers() {
            for (const timer of this.loaderTimers) {
                clearTimeout(timer);
            }
            this.loaderTimers = [];
        },

        startLoaderSequence() {
            this.clearLoaderTimers();
            this.loaderState = {
                s1: false,
                s2: false,
                s3: false
            };
            this.loaderTimers.push(setTimeout(() => {
                if (this.step === 'loadingQuote' || this.step === 'address') this.loaderState.s1 = true;
            }, 150));
            this.loaderTimers.push(setTimeout(() => {
                if (this.step === 'loadingQuote' || this.step === 'address') this.loaderState.s2 = true;
            }, 700));
            this.loaderTimers.push(setTimeout(() => {
                if (this.step === 'loadingQuote' || this.step === 'address') this.loaderState.s3 = true;
            }, 1250));
        }
    });

        if (typeof window !== 'undefined') {
            window.__ashpJourneyState = state;
        }

        return state;
    };

    if (typeof window !== 'undefined') {
        window.ashpJourney = createStore;
    }

    const refreshAlpine = () => {
        if (!window.Alpine || typeof window.Alpine.initTree !== 'function') return;
        const root = document.querySelector('[data-journey="ashp-journey"]');
        if (root) window.Alpine.initTree(root);
    };

    const ensureMounted = () => {
        if (!window.Alpine || typeof window.Alpine.initTree !== 'function') return;
        const root = document.querySelector('[data-journey="ashp-journey"]');
        if (root) {
            window.Alpine.initTree(root);
            return;
        }
        const observer = new MutationObserver(() => {
            const nextRoot = document.querySelector('[data-journey="ashp-journey"]');
            if (!nextRoot) return;
            window.Alpine.initTree(nextRoot);
            observer.disconnect();
        });
        observer.observe(document.documentElement, { childList: true, subtree: true });
        setTimeout(() => observer.disconnect(), 5000);
    };

    const register = () => {
        if (!window.Alpine || typeof window.Alpine.data !== 'function') return;
        window.Alpine.data('ashpJourney', createStore);
        refreshAlpine();
    };

    if (window.Alpine) register();
    else document.addEventListener('alpine:init', register);

    document.addEventListener('DOMContentLoaded', refreshAlpine);
    document.addEventListener('DOMContentLoaded', ensureMounted);
}

registerAshpJourney();
